import { Icon } from '@euler/components/Icomoon';
import Euler, { EulerViewProps } from '@euler/components/layout';
import { FontFamily } from '@euler/components/typography';
import { PredefinedHitSlops } from '@euler/functions/predefinedHitSlops';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { OrderFieldVisibility } from '@euler/model';
import { SafeHaptics } from '@euler/utils';
import * as Clipboard from 'expo-clipboard';
import { ReactNode, memo, useCallback, useMemo } from 'react';
import {
  StyleProp,
  StyleSheet,
  Text,
  TextProps,
  TextStyle,
  ViewStyle,
} from 'react-native';
import Toast from 'react-native-root-toast';
import invariant from 'tiny-invariant';
import { shouldDisplayField } from './util';

export type OrderSummaryInfoRowProps<T> = {
  label: string;
  value: T;
  visibility?: OrderFieldVisibility;
  defaultVisible?: boolean;
  copyable?: boolean | string | null;
  fallbackText?: string;
  style?: StyleProp<ViewStyle>;
  contentStyle?: StyleProp<ViewStyle>;
  labelStyle?: StyleProp<TextStyle>;
  textStyle?: StyleProp<TextStyle>;
  children?: ReactNode | ((node: ReactNode) => ReactNode);
  format?: (value: NonNullable<T>, style: StyleProp<TextStyle>) => ReactNode;
  valid?: (value: T) => boolean;
};

function OrderSummaryInfoRowUnmemo<T>({
  label,
  value,
  valid,
  format,
  fallbackText,
  visibility,
  defaultVisible,
  labelStyle,
  textStyle,
  contentStyle,
  children,
  copyable,
  ...props
}: OrderSummaryInfoRowProps<T> & Omit<EulerViewProps, 'children'>) {
  const isVisible = shouldDisplayField({
    value,
    visibility,
    defaultVisible,
    valid,
  });

  const { rpx } = useResponsiveContext();

  const onCopy = useCallback(async () => {
    if (!copyable) return false;
    const text =
      typeof copyable === 'string'
        ? copyable
        : typeof value === 'string'
        ? value
        : undefined;
    invariant(text != null, 'text to copy is null or empty');
    await Clipboard.setStringAsync(text);
    Toast.show('已复制', {
      duration: 500,
      position: Toast.positions.CENTER,
    });
    SafeHaptics.impact();
  }, [value, copyable]);

  const renderDefaultChildren = () => {
    return (
      <SecondaryInfoText style={[textStyle]} numberOfLines={0}>
        {value == null
          ? fallbackText ?? ''
          : format?.(value, textStyle) ?? String(value)}
      </SecondaryInfoText>
    );
  };

  if (!isVisible) {
    return null;
  }

  return (
    <Euler.View
      flexDirection="row"
      justifyContent="flex-start"
      alignItems="center"
      mb={rpx(6.5)}
      {...props}
    >
      <SecondaryInfoText isLabel style={labelStyle}>
        {`${label}:`}
      </SecondaryInfoText>
      <Euler.View
        flexDirection="row"
        justifyContent="flex-start"
        alignItems="flex-start"
        ml={rpx(4)}
        style={contentStyle}
      >
        {typeof children === 'function'
          ? children(renderDefaultChildren())
          : children ?? renderDefaultChildren()}
        {copyable ? (
          <Euler.TouchableOpacity
            onPress={onCopy}
            hitSlop={PredefinedHitSlops.large}
            ml={rpx(8)}
            top={rpx(3)}
          >
            <Icon name="copy" size={rpx(13)} color="#ccc" />
          </Euler.TouchableOpacity>
        ) : null}
      </Euler.View>
    </Euler.View>
  );
}

export const OrderSummaryInfoRow = memo(
  OrderSummaryInfoRowUnmemo,
) as typeof OrderSummaryInfoRowUnmemo;

const SecondaryInfoText = memo(
  ({
    numberOfLines,
    isLabel,
    style,
    ...props
  }: TextProps & { isLabel?: boolean }) => {
    const styles = useStyles();
    return (
      <Text
        numberOfLines={numberOfLines ?? 1}
        style={[
          styles.secondaryInfoText,
          isLabel ? styles.secondaryInfoLabel : {},
          style,
        ]}
        {...props}
      />
    );
  },
);

const useStyles = () => {
  const { rpx } = useResponsiveContext();
  return useMemo(
    () =>
      StyleSheet.create({
        secondaryInfoText: {
          fontFamily: FontFamily.NotoSans.Regular,
          fontSize: rpx(14),
          lineHeight: rpx(18),
          color: '#333',
        },
        secondaryInfoLabel: {
          fontFamily: FontFamily.NotoSans.Light,
          fontSize: rpx(15),
          color: '#777',
        },
      }),
    [rpx],
  );
};
